import React, { useState, useEffect, useRef } from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { FEATURES } from "src/config/features"

// Account sidebar — ported from the 3C design prototype (.side / .side-nav).
// Keeps the existing 10 profile routes; labels are i18n keys (sidebar.*).
// Active detection mirrors the old ProfileLayout logic. On desktop it renders
// the vertical sidebar; on mobile (<=880px) it collapses into a dropdown picker.
const MENU = [
    { tkey: "sidebar.myProfile", path: "/profile", d: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM5 21a7 7 0 0 1 14 0" },
    { tkey: "sidebar.myOrders", path: "/profile/orders", d: "M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0" },
    { tkey: "sidebar.coinTransactions", path: "/profile/credit-transactions", d: "M2 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zM16 12h4" },
    // "My Redemptions" — the standalone redemptions list was merged in here (keeps its gift icon).
    { tkey: "sidebar.myRedemptions", path: "/profile/airtag-points", d: "M20 12v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" },
    // "Mystery Draws and History" — the Treasure Bag Play History page was merged in here.
    { tkey: "sidebar.mysteryDraws", path: "/profile/treasure-points", d: "M12 2 4 7l8 13 8-13zM4 7h16M9 7l3 13M15 7l-3 13" },
    { tkey: "sidebar.vipPoints", path: "/profile/vip-points", vip: true, d: "M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM8.5 13.5 7 22l5-3 5 3-1.5-8.5" },
    // "My Referrals" — Referral Coins was merged in at the bottom of this page.
    { tkey: "sidebar.myReferrals", path: "/profile/referrals", d: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" },
    // Lucide "message-circle", hand-inlined as one path: navIcon renders a single
    // <path>, and the section is a plain help desk, not a shopping surface, so it
    // sits last. startsWith() keeps it lit on /profile/support/new too.
    { tkey: "sidebar.support", path: "/profile/support", d: "M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" },
]

const navIcon = (d) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d={d} />
    </svg>
)

export default function AccountSidebar() {
    const location = useLocation()
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    const isActive = (path) =>
        path === "/profile" ? location.pathname === "/profile" : location.pathname.startsWith(path)

    // hide VIP entries while the VIP campaign feature is off
    const menuItems = MENU.filter((item) => !item.vip || FEATURES.vipCampaign)

    const current = menuItems.find((item) => isActive(item.path)) || menuItems[0]

    // close the dropdown on navigation
    useEffect(() => {
        setOpen(false)
    }, [location.pathname])

    // close the dropdown on outside click
    useEffect(() => {
        if (!open) return
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener("mousedown", onDown)
        return () => document.removeEventListener("mousedown", onDown)
    }, [open])

    return (
        <aside className="side" ref={ref}>
            {/* mobile trigger — shows the current section */}
            <button
                type="button"
                className={`side-toggle${open ? " open" : ""}`}
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
            >
                {navIcon(current.d)}
                {/* Static label: the page's own <h1> already names the section, so
                    repeating it here was redundant. The control itself stays — it is
                    the only account navigation below 880px. */}
                <span className="lbl">{t("sidebar.menu")}</span>
                <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>

            <div className="side-head">{t("sidebar.title")}</div>

            <nav className={`side-nav${open ? " open" : ""}`}>
                {menuItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={isActive(item.path) ? "active" : ""}
                        onClick={() => setOpen(false)}
                    >
                        {navIcon(item.d)}
                        {t(item.tkey)}
                    </Link>
                ))}
            </nav>
        </aside>
    )
}
